import React, { useState, useRef } from 'react';
import {X, Calculator, RotateCcw, Download, AlertTriangle, Waves} from 'lucide-react';

interface FlujoCriticoTrapecialCalculatorProps {
  onClose: () => void;
}

interface Results {
  yc: number;
  an: number;
  pn: number;
  rh: number;
  te: number;
  vel: number;
  fr: number;
  ener: number;
  tflujo: string;
}

const FlujoCriticoTrapecialCalculator: React.FC<FlujoCriticoTrapecialCalculatorProps> = ({ onClose }) => {
  const [gasto, setGasto] = useState('');
  const [ancho, setAncho] = useState('');
  const [talud, setTalud] = useState('');
  const [rugosidad, setRugosidad] = useState('');
  const [pendiente, setPendiente] = useState('');
  const [results, setResults] = useState<Results | null>(null);
  const [error, setError] = useState<string | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  const calcular = () => {
    setError(null);
    const q = Number(gasto);
    const b = Number(ancho);
    const k = Number(talud);
    const n = Number(rugosidad);
    const s = Number(pendiente);

    if (q === 0 || isNaN(q) || n === 0 || isNaN(n) || s < 0 || isNaN(s) || (b === 0 && k === 0)) {
      setError('Faltan datos o son incorrectos. Verifique los valores ingresados.');
      setResults(null);
      return;
    }

    const theta = Math.atan(s);
    let yn: number;
    let an: number;
    let pn: number;
    let rh: number;

    if (b === 0) {
      // Sección Triangular
      yn = Math.pow(((Math.pow(q, 2) * 2) / ((Math.pow(k, 2)) * 9.81 * Math.cos(theta))), (1 / 5));
      an = (k * yn) * yn;
      pn = 2 * yn * Math.pow((Math.pow(k, 2) + 1), 0.5);
      rh = an / pn;
    } else {
      // Sección Trapecial / Rectangular - Newton-Raphson
      yn = 1;
      const pn1 = Math.pow(q, 2) / (9.81 * Math.cos(theta));
      let pn2 = 100;
      let asl = 0;
      let iter = 0;
      const MAX_ITER = 200;
      an = 0;
      pn = 0;
      rh = 0;

      while (Math.abs(pn1 - pn2) >= 0.00000011 && iter < MAX_ITER) {
        an = (b + k * yn) * yn;
        pn = b + 2 * yn * Math.pow((Math.pow(k, 2) + 1), 0.5);
        rh = an / pn;
        asl = b + 2 * k * yn;
        pn2 = Math.pow(an, 3) / asl;
        const pn3 = (3 * Math.pow(an, 2)) - (2 * k) * (Math.pow(an, 3)) / Math.pow(asl, 2);
        const ptn = (pn2 - pn1) / pn3;
        yn = yn - ptn;
        iter++;
      }

      if (iter >= MAX_ITER || !isFinite(yn) || yn <= 0) {
        setError('No se pudo converger a una solución. Verifique los valores ingresados.');
        setResults(null);
        return;
      }
    }

    const te = b + 2 * k * yn;
    const vel = q / an;
    const fr = vel / Math.pow((9.81 * an / te), 0.5);
    const ener = yn * Math.cos(theta) + Math.pow(vel, 2) / (2 * 9.81);
    const tflujo = fr > 1 ? 'Supercrítico' : 'Subcrítico';

    setResults({
      yc: Math.trunc(yn * 10000) / 10000,
      an: Math.trunc(an * 10000) / 10000,
      pn: Math.trunc(pn * 10000) / 10000,
      rh: Math.trunc(rh * 10000) / 10000,
      te: Math.trunc(te * 10000) / 10000,
      vel: Math.trunc(vel * 10000) / 10000,
      fr: Math.trunc(fr * 10000) / 10000,
      ener: Math.trunc(ener * 10000) / 10000,
      tflujo
    });
  };

  const limpiar = () => {
    setGasto('');
    setAncho('');
    setTalud('');
    setRugosidad('');
    setPendiente('');
    setResults(null);
    setError(null);
  };

  const exportarPNG = async () => {
    if (!exportRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `flujo-critico-trapecial-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error exportando PNG:', err);
      alert('No se pudo exportar la imagen. Intente nuevamente.');
    }
  };

  const tipoSeccion = () => {
    const b = Number(ancho);
    const k = Number(talud);
    if (b === 0 && k > 0) return 'Triangular';
    if (b > 0 && k === 0) return 'Rectangular';
    if (b > 0 && k > 0) return 'Trapecial';
    return '—';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8 overflow-hidden">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
              <Waves className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Flujo Crítico</h2>
              <p className="text-amber-100 text-sm">Trapecial · Rectangular · Triangular</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div ref={exportRef} className="bg-white">
          {/* Description */}
          <div className="px-6 pt-6 pb-2">
            <p className="text-sm text-gray-600 leading-relaxed">
              Ingrese los datos para el diseño hidráulico de canales <strong>trapezoidales, rectangulares o triangulares</strong> en condiciones de flujo crítico.
              Para sección rectangular use <strong>K = 0</strong>; para sección triangular use <strong>B = 0</strong>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            {/* Inputs */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-100">
              <h3 className="text-amber-700 font-bold text-center mb-4 text-sm uppercase tracking-wider">
                — Datos a ingresar —
              </h3>

              {/* Diagrama dinamico de la seccion del canal */}
              <div className="mb-4">
                <ChannelSectionDiagram
                  base={Number(ancho) || 0}
                  talud={Number(talud) || 0}
                  tirante={results?.yc || 0}
                />
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Q = Gasto del canal (m³/s)
                  </label>
                  <input
                    type="number"
                    value={gasto}
                    onChange={(e) => setGasto(e.target.value)}
                    min="0"
                    max="10000"
                    step="any"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Ej. 2.5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    B = Ancho de base o plantilla (m)
                  </label>
                  <input
                    type="number"
                    value={ancho}
                    onChange={(e) => setAncho(e.target.value)}
                    min="0"
                    max="10000"
                    step="any"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="0 para triangular"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    K = Talud (Adimensional)
                  </label>
                  <input
                    type="number"
                    value={talud}
                    onChange={(e) => setTalud(e.target.value)}
                    min="0"
                    max="10000"
                    step="any"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="0 para rectangular"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    n = Rugosidad (Adimensional)
                  </label>
                  <input
                    type="number"
                    value={rugosidad}
                    onChange={(e) => setRugosidad(e.target.value)}
                    min="0"
                    max="10000"
                    step="any"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Ej. 0.013"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    So = Pendiente (m/m)
                  </label>
                  <input
                    type="number"
                    value={pendiente}
                    onChange={(e) => setPendiente(e.target.value)}
                    min="0"
                    max="10000"
                    step="any"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Ej. 0.001"
                  />
                </div>
                <div className="pt-2 text-xs text-gray-500">
                  Sección detectada: <span className="font-semibold text-amber-700">{tipoSeccion()}</span>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-5 border border-gray-200">
              <h3 className="text-amber-700 font-bold text-center mb-4 text-sm uppercase tracking-wider">
                ——— Resultados ———
              </h3>
              <div className="space-y-2">
                {[
                  { label: 'Tirante Crítico (m)', value: results?.yc },
                  { label: 'Área Hidráulica (m²)', value: results?.an },
                  { label: 'Perímetro Mojado (m)', value: results?.pn },
                  { label: 'Radio Hidráulico (m)', value: results?.rh },
                  { label: 'Espejo del Agua (m)', value: results?.te },
                  { label: 'Velocidad (m/s)', value: results?.vel },
                  { label: 'Núm. de Froude (Adim.)', value: results?.fr },
                  { label: 'Energía Específica (m)', value: results?.ener }
                ].map((r) => (
                  <div key={r.label} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                    <span className="text-xs text-gray-600 font-medium">{r.label}</span>
                    <span className="text-sm font-bold text-gray-900 font-mono">
                      {r.value !== undefined ? r.value : '—'}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                  <span className="text-xs text-gray-600 font-medium">Tipo de flujo</span>
                  <span className={`text-sm font-bold ${
                    results?.tflujo === 'Supercrítico' ? 'text-red-600' :
                    results?.tflujo === 'Subcrítico' ? 'text-blue-600' : 'text-gray-400'
                  }`}>
                    {results?.tflujo ?? '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mx-6 mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Footer brand for export */}
          <div className="px-6 pb-4 text-center">
            <p className="text-xs text-gray-400">
              Generado por <span className="font-semibold text-amber-700">openHH</span> · HHTools — {new Date().toLocaleString('es-MX')}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-wrap gap-3 justify-center">
          <button
            onClick={calcular}
            className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold px-6 py-2.5 rounded-lg shadow-md transition-all"
          >
            <Calculator className="w-4 h-4" />
            Calcular
          </button>
          <button
            onClick={limpiar}
            className="flex items-center gap-2 bg-white hover:bg-gray-100 text-gray-700 font-semibold px-6 py-2.5 rounded-lg border border-gray-300 transition-all"
          >
            <RotateCcw className="w-4 h-4" />
            Limpiar
          </button>
          <button
            onClick={exportarPNG}
            disabled={!results}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5 rounded-lg shadow-md transition-all"
          >
            <Download className="w-4 h-4" />
            Exportar PNG
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Diagrama SVG dinamico de la seccion transversal del canal abierto.
 * Refleja en tiempo real B (base), K (talud) y el tirante critico (Yc calculado).
 * Conmuta automaticamente entre seccion triangular (B=0), rectangular (K=0) y trapecial.
 */
const ChannelSectionDiagram: React.FC<{ base: number; talud: number; tirante: number }> = ({
  base,
  talud,
  tirante,
}) => {
  const W = 360;
  const H = 200;
  const padX = 50;
  const baseY = H - 45;

  const tipo = base <= 0 ? 'Triangular' : talud <= 0 ? 'Rectangular' : 'Trapecial';

  // Valores de referencia para dibujar de forma estable cuando faltan datos
  const b = base > 0 ? base : 0;
  const k = talud > 0 ? talud : 0;
  const yRef = tirante > 0 ? tirante : 1;

  // Anchos geometricos (en unidades reales)
  const topWidth = b + 2 * k * yRef; // espejo del agua
  const maxWidth = Math.max(topWidth, b, 0.001);

  // Escalas para encajar el dibujo en el viewBox
  const usableW = W - padX * 2;
  const usableH = baseY - 40;
  const scaleX = usableW / maxWidth;
  const scaleY = usableH / yRef;
  const scale = Math.min(scaleX, scaleY);

  const cx = W / 2;
  const bottomHalf = (b * scale) / 2;
  const topHalf = (topWidth * scale) / 2;
  const waterDepth = yRef * scale;
  const waterTopY = baseY - waterDepth;

  // Puntos del canal
  const blX = cx - bottomHalf;
  const brX = cx + bottomHalf;
  const tlX = cx - topHalf;
  const trX = cx + topHalf;

  const fmt = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');

  // Trazo de las paredes del canal (margen un poco mas alto que el agua)
  const wallTopY = waterTopY - 16;
  const wallTlX = tlX - (k > 0 ? (16 / (scaleY || 1)) * k * scale : 0);
  const wallTrX = trX + (k > 0 ? (16 / (scaleY || 1)) * k * scale : 0);

  const waterPath = `M ${blX},${baseY} L ${tlX},${waterTopY} L ${trX},${waterTopY} L ${brX},${baseY} Z`;
  const channelPath = `M ${wallTlX},${wallTopY} L ${blX},${baseY} L ${brX},${baseY} L ${wallTrX},${wallTopY}`;

  return (
    <div className="bg-white rounded-lg border border-amber-200 overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-3 py-2 bg-amber-50 border-b border-amber-100">
        <span className="text-xs font-semibold text-amber-800">Seccion transversal del canal</span>
        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white text-amber-700 border border-amber-300">
          {tipo}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="critTrapWaterGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#ea580c" stopOpacity="0.9" />
          </linearGradient>
        </defs>

        {/* Terreno / paredes del canal */}
        <path d={channelPath} fill="none" stroke="#374151" strokeWidth="3" strokeLinejoin="round" />

        {/* Agua */}
        <path d={waterPath} fill="url(#critTrapWaterGrad)" stroke="#c2410c" strokeWidth="1" />
        {topHalf > 6 && (
          <path
            d={`M ${tlX},${waterTopY} Q ${cx},${waterTopY - 3} ${trX},${waterTopY}`}
            fill="none"
            stroke="#ffffff"
            strokeWidth="1"
            opacity="0.7"
          />
        )}

        {/* Cota tirante Yc */}
        <g>
          <line x1={brX + 24} y1={waterTopY} x2={brX + 24} y2={baseY} stroke="#dc2626" strokeWidth="1.2" />
          <polygon points={`${brX + 24},${waterTopY} ${brX + 20},${waterTopY + 6} ${brX + 28},${waterTopY + 6}`} fill="#dc2626" />
          <polygon points={`${brX + 24},${baseY} ${brX + 20},${baseY - 6} ${brX + 28},${baseY - 6}`} fill="#dc2626" />
          <text x={brX + 30} y={(waterTopY + baseY) / 2 + 4} fill="#b91c1c" fontSize="11" fontWeight="700">
            {tirante > 0 ? `Yc=${fmt(tirante)}m` : 'Yc'}
          </text>
        </g>

        {/* Cota base B */}
        {b > 0 && (
          <g>
            <line x1={blX} y1={baseY + 16} x2={brX} y2={baseY + 16} stroke="#1d4ed8" strokeWidth="1.2" />
            <polygon points={`${blX},${baseY + 16} ${blX + 6},${baseY + 12} ${blX + 6},${baseY + 20}`} fill="#1d4ed8" />
            <polygon points={`${brX},${baseY + 16} ${brX - 6},${baseY + 12} ${brX - 6},${baseY + 20}`} fill="#1d4ed8" />
            <text x={cx} y={baseY + 30} textAnchor="middle" fill="#1e40af" fontSize="11" fontWeight="700">
              B = {fmt(base)} m
            </text>
          </g>
        )}

        {/* Etiqueta talud K */}
        {k > 0 && tirante >= 0 && (
          <text x={tlX - 6} y={(waterTopY + baseY) / 2} textAnchor="end" fill="#c2410c" fontSize="10" fontWeight="700">
            K = {fmt(talud)}
          </text>
        )}
      </svg>
    </div>
  );
};

export default FlujoCriticoTrapecialCalculator;
